import { urlParser } from '@hh.ru/browser-api-utils';

import {
    EdProgramAgeInterval,
    EdProgramCategory,
    EdProgramFilter,
    EdProgramFilterAvailableSortDto,
    EdProgramFilterItem,
    EdProgramFilterItemKey,
    EdProgramFilterItemKeyTypeMap,
    EdProgramFilterQuery,
    EdProgramFilterQueryForFiltersShard,
    EdProgramFilterQueryForListShard,
    EdProgramFilterType,
    EdProgramFormatFilter,
    EdProgramPriceInterval,
    EdProgramCoursePurpose,
    EdProgramCoursePurposeFilter,
    EdProgramEducationLevelFilter,
    EdProgramPurpose,
} from 'src/types/EdProgram';

import { ED_PROGRAMS_ROUTE } from 'src/app/routesUtils/urls';

const OMIT_SORT_VALUES = [EdProgramFilterAvailableSortDto.Default, EdProgramFilterAvailableSortDto.Relevant];

const toFlatObject = (params: EdProgramFilterQuery): Record<string, string> => {
    const compacted = Object.entries(params)
        .map(([key, value]: [string, string | string[]]) =>
            Array.isArray(value) ? [key, value.join(',')] : [key, value]
        )
        .filter(([, value]) => (Array.isArray(value) ? value.length : value)) as [string, string][];

    return compacted.reduce<Record<string, string>>((acc, [key, value]: [string, string]) => {
        acc[key] = value;
        return acc;
    }, {});
};

const toQueryString = (params: EdProgramFilterQuery): string => {
    return urlParser.stringify(toFlatObject(params));
};

export class EdProgramFilterParams {
    private filter: EdProgramFilter;
    private category: EdProgramCategory;
    private sort: EdProgramFilterAvailableSortDto;
    private text?: string;

    constructor(
        courseFilter: EdProgramFilter,
        category: EdProgramCategory,
        sort: EdProgramFilterAvailableSortDto,
        text?: string
    ) {
        this.filter = courseFilter;
        this.category = category;
        this.sort = sort;
        this.text = text;
    }

    static onlySelected(filter: EdProgramFilter): Record<EdProgramFilterItemKey, EdProgramFilterItem[]> {
        return Object.entries(filter).reduce(
            (acc, [key, values]) => {
                const filterItemKey = key as EdProgramFilterItemKey;

                acc[filterItemKey] = values.filter((item, index) => {
                    // Убираем дефолтные значения для радио из итогового списка
                    if (
                        EdProgramFilterItemKeyTypeMap[filterItemKey] === EdProgramFilterType.Radio &&
                        item.state.selected &&
                        index === 0
                    ) {
                        return false;
                    }
                    return item.state.selected;
                });
                return acc;
            },
            {} as Record<EdProgramFilterItemKey, EdProgramFilterItem[]>
        );
    }

    static getEdProgramsHref = (edProgramsFilterQuery: EdProgramFilterQuery = {}): string => {
        const url = urlParser(ED_PROGRAMS_ROUTE);
        url.search = toQueryString({ ...edProgramsFilterQuery });
        return url.href;
    };

    private asEdProgramsCatalogFilterQuery(): EdProgramFilterQuery {
        const onlySelected = EdProgramFilterParams.onlySelected(this.filter);

        const filters: Required<Omit<EdProgramFilterQuery, 'sort'>> = {
            category: this.category,
            purpose: onlySelected?.filterPurposes?.[0]?.value as EdProgramCoursePurposeFilter,
            priceParameters: onlySelected?.filterPriceParameters?.map((item) => item.value) as EdProgramPriceInterval[],
            educationLevel: onlySelected?.filterEducationLevel?.[0]?.value as EdProgramEducationLevelFilter,
            //  Для случая "разработчик c#" и обработки `#` добавлен encodeURIComponent здесь и unquote в python
            text: encodeURIComponent(this.text || ''),
            ageLimit: onlySelected?.filterAge?.[0]?.value as EdProgramAgeInterval,
            format: onlySelected?.filterFormat?.[0]?.value as EdProgramFormatFilter,
            areaIds: onlySelected?.filterAreas?.map((item) => item.value),
            streamIds: onlySelected?.filterStreams?.map((item) => item.value),
            skillIds: onlySelected?.filterSkills?.map((item) => item.value),
            providerIds: onlySelected?.filterProviders?.map((item) => item.value),
            duration: onlySelected?.filterDurations?.map((item) => item.value),
            professionIds: onlySelected?.filterProfessions?.map((item) => item.value),
        };

        const filterQueryParams: EdProgramFilterQuery = filters;

        if (!OMIT_SORT_VALUES.includes(this.sort)) {
            filterQueryParams.sort = this.sort;
        }

        // TODO: временный костыль чтобы не отправлять навыки если выбран тип профессия
        if (filterQueryParams.purpose === EdProgramCoursePurpose.Profession) {
            filterQueryParams.skillIds = [];
        }

        return filterQueryParams;
    }

    asParamsForList(purpose: EdProgramPurpose, page = 0): EdProgramFilterQueryForListShard {
        const courseFilterQuery = this.asEdProgramsCatalogFilterQuery();
        const asObject: EdProgramFilterQuery = courseFilterQuery;

        return { ...toFlatObject(asObject), category: this.category, purpose, page };
    }

    asParamsForFilters(): EdProgramFilterQueryForFiltersShard {
        const courseFilterQuery = this.asEdProgramsCatalogFilterQuery();
        const asObject: EdProgramFilterQuery = courseFilterQuery;

        return { ...toFlatObject(asObject), category: this.category };
    }

    asEdProgramsHref(): string {
        return EdProgramFilterParams.getEdProgramsHref(this.asEdProgramsCatalogFilterQuery());
    }

    asAnalyticsParams = (): EdProgramFilterQuery => {
        return this.asEdProgramsCatalogFilterQuery();
    };
}
